import React, { useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Plus, ShieldCheck, Clock, CreditCard, Headphones, MapPin } from "lucide-react";
import { useApp, formatEUR } from "@/context/AppContext";
import { getProduct, ALL_PRODUCTS, categoryOf } from "@/data/products";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const trustIcons = [Clock, CreditCard, ShieldCheck, Headphones];

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang, addToCart, setCartOpen } = useApp();
  const product = getProduct(id);
  const [active, setActive] = useState(0);

  const related = useMemo(() => {
    if (!product) return [];
    return ALL_PRODUCTS.filter((p) => p.id !== id && categoryOf(p) === categoryOf(product)).slice(0, 3);
  }, [id, product]);

  if (!product) {
    return (
      <div className="mx-auto max-w-3xl px-6 py-24 text-center" data-testid="product-not-found">
        <p className="text-lg text-slate-600">404</p>
        <Button asChild className="mt-4 rounded-full bg-cea-accent text-white hover:bg-cea-navy"><Link to="/catalogue">{t.detail.back}</Link></Button>
      </div>
    );
  }

  const isVehicle = categoryOf(product) === "location";
  const unitLabel = product.unit === "day" ? t.perDay : product.unit === "forfait" ? ` ${t.forfait}` : ` ${t.unitPrice}`;

  const handleAdd = () => {
    addToCart(product.id, 1);
    toast.success(`${product.name[lang]} — ${t.cta.added}`, { action: { label: t.cart.title, onClick: () => setCartOpen(true) } });
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-10 sm:px-8" data-testid={`product-detail-${product.id}`}>
      <button onClick={() => navigate(isVehicle ? "/flotte" : "/catalogue")} data-testid="detail-back" className="mb-6 inline-flex items-center gap-1.5 text-sm font-medium text-slate-500 transition-colors hover:text-cea-navy">
        <ArrowLeft className="h-4 w-4" />{isVehicle ? t.detail.backFleet : t.detail.back}
      </button>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
        {/* Gallery */}
        <div>
          <motion.div key={active} initial={{ opacity: 0.4 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }} className="aspect-[4/3] overflow-hidden rounded-3xl bg-slate-100">
            <img src={product.images[active]} alt={product.name[lang]} className="h-full w-full object-cover" data-testid="detail-main-image" />
          </motion.div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {product.images.map((src, i) => (
              <button key={i} onClick={() => setActive(i)} data-testid={`detail-thumb-${i}`} className={`aspect-[4/3] overflow-hidden rounded-xl border-2 transition-all ${active === i ? "border-cea-accent" : "border-transparent opacity-70 hover:opacity-100"}`}>
                <img src={src} alt={`${product.name[lang]} ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
          {isVehicle && product.city && (
            <p className="mt-3 inline-flex items-center gap-1.5 text-xs text-slate-400"><MapPin className="h-3.5 w-3.5" />{t.detail.photographed} {product.city}</p>
          )}
        </div>

        {/* Info */}
        <div className="flex flex-col">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cea-accent">{isVehicle ? t.nav.fleet : t.catalogue.title}</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-cea-ink sm:text-4xl">{product.name[lang]}</h1>
          <p className="mt-2 text-lg text-slate-500">{product.tagline[lang]}</p>

          <div className="mt-5 flex items-end gap-2">
            <span className="text-xs font-medium text-slate-400">{t.detail.from}</span>
            <span className="text-3xl font-extrabold text-cea-navy" data-testid="detail-price">{formatEUR(product.price)}</span>
            <span className="mb-1 text-sm font-medium text-slate-400">{unitLabel}</span>
          </div>

          {isVehicle && (
            <div className="mt-5 rounded-2xl border border-slate-200 bg-cea-surface p-4">
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-cea-accent">{t.detail.brands}</p>
              <p className="mt-1 text-sm font-medium text-slate-600" data-testid="detail-brands">{product.brands}</p>
            </div>
          )}

          <ul className="mt-5 grid grid-cols-2 gap-2.5">
            {product.specs[lang].map((s, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-slate-700"><Check className="h-4 w-4 shrink-0 text-cea-accent" />{s}</li>
            ))}
          </ul>

          <div className="mt-6 flex flex-wrap gap-3">
            <Button onClick={handleAdd} data-testid="detail-add-to-cart" className="rounded-full bg-cea-accent px-8 py-6 text-base font-semibold text-white hover:bg-cea-navy">
              <Plus className="mr-1.5 h-5 w-5" />{product.unit === "day" ? t.cta.rent : t.cta.addToCart}
            </Button>
            <Button asChild variant="outline" className="rounded-full border-cea-navy px-8 py-6 text-base font-semibold text-cea-navy hover:bg-slate-50">
              <Link to="/devis" data-testid="detail-quote">{t.cta.getQuote}</Link>
            </Button>
          </div>

          <div className="mt-6 grid grid-cols-2 gap-3 border-t border-slate-200 pt-5 sm:grid-cols-4">
            {t.detail.trust.map((tr, i) => {
              const Icon = trustIcons[i];
              return (
                <div key={i} className="flex flex-col items-start gap-1.5 text-xs text-slate-500">
                  <Icon className="h-4 w-4 text-cea-accent" />{tr}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Description */}
      <section className="mt-14 max-w-3xl">
        <h2 className="text-xl font-bold text-cea-navy">{t.detail.why}</h2>
        <p className="mt-3 text-base leading-relaxed text-slate-600" data-testid="detail-description">{product.description[lang]}</p>
      </section>

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="mb-6 text-xl font-bold text-cea-navy">{t.detail.related}</h2>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
